import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  CircularProgress, 
  Alert, 
  Paper,
  Chip,
  Divider
} from '@mui/material';
import { getPublicJobsWithCoverage } from '../services/api';
import { JobWithCoverage } from '../models/types';
import JobCard from '../components/JobCard';
import AssignmentIcon from '@mui/icons-material/Assignment';

const JobsListPage: React.FC = () => {
  const [jobs, setJobs] = useState<JobWithCoverage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        setLoading(true);
        const data = await getPublicJobsWithCoverage();
        const sortedJobs = data.sort((a, b) => a.name.localeCompare(b.name));
        setJobs(sortedJobs);
        setError(null);
      } catch (err) {
        console.error('Error fetching jobs:', err);
        setError('Failed to load available jobs. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchJobs();
  }, []);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Lade verfügbare Dienste...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (jobs.length === 0) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>        <Alert severity="info">          Derzeit sind keine aktiven Dienste verfügbar. Bitte schauen Sie später wieder vorbei.
        </Alert>
      </Container>
    );
  }
  const calculateSummary = () => {
    if (jobs.length === 0) return null;
    
    const totalJobs = jobs.length;
    const totalPositions = jobs.reduce((sum, job) => sum + job.requiredVolunteers, 0);
    const urgentJobs = jobs.filter(job => job.coverage < 50).length;
    
    return { totalJobs, totalPositions, urgentJobs };
  };
  
  const summary = calculateSummary();

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Verfügbare Dienste
        </Typography>
        
        {summary && (
          <Paper elevation={3} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
              <Box>
                <Typography variant="h6" gutterBottom>
                  Übersicht der Helferpositionen
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Hier finden Sie alle Dienste, für die aktuell Helfer gesucht werden
                  Bei Fragen kontaktiere gerne Katharina Aigner (0664 3953799) oder Mathias Kamleitner (0676 6266084).
                </Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  <Chip 
                    icon={<AssignmentIcon />}
                    label={`${summary.totalJobs} Dienste`} 
                    color="primary" 
                    variant="outlined"
                  />
                  {summary.urgentJobs > 0 && (
                    <Chip 
                      label={`${summary.urgentJobs} dringend Helfer benötigt`} 
                      color="error" 
                    />
                  )}
                </Box>
              </Box>
            </Box>
          </Paper>
        )}
        
        <Typography variant="body1" paragraph>
          Durchsuchen und anmelden für verfügbare Helferpositionen. Positionen mit roter Markierung benötigen dringend Helfer.
        </Typography>
        
        <Divider sx={{ my: 3 }} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {jobs.map((job) => (
            <Box key={job.id} sx={{ flexBasis: { xs: '100%', sm: 'calc(50% - 32px)', md: 'calc(33.333% - 32px)' }, flexGrow: 0 }}>
              <JobCard job={job} />
            </Box>
          ))}
        </Box>
      </Box>
    </Container>
  );
};

export default JobsListPage;

